// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file Icon.tsx
 * @input Uses ReactSVGProps, icon components or semantic icon names
 * @output Exports Icon component, IconProps, IconColor, IconSize, IconType types
 * @position Core implementation; consumed by index.ts, tested by Icon.test.tsx
 *
 * Supports two modes:
 * - Component mode: Pass an SVG icon component (e.g. from @heroicons/react) — rendered
 *   directly with and spread SVG props.
 * - String mode: Pass a semantic name (e.g. 'close', 'chevronDown') — resolved from the
 *   theme's icon registry (or built-in fallback SVGs) and wrapped in a styled span.
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Icon/Icon.doc.mjs (props table, features, implementation notes)
 * - /packages/core/src/Icon/Icon.test.tsx (tests for new/changed behavior)
 * - /packages/core/src/Icon/index.ts (exports if types change)
 * - /apps/storybook/stories/Icon.stories.tsx (storybook stories)
 * - /packages/cli/templates/blocks/components/Icon/ (showcase blocks)
 */
import React from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars } from "../theme/tokens.stylex.js";
import { getIcon } from "./globalIconRegistry.js";
import { mergeProps } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";

// =============================================================================
// Styles
// =============================================================================
import { jsx as _jsx } from "react/jsx-runtime";
const styles = {
  root: {
    kmuXW: "x2lah0s",
    $$css: true
  },
  span: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kmuXW: "x2lah0s",
    $$css: true
  }
};
const colorStyles = {
  primary: {
    kMwMTN: "xtbr613",
    $$css: true
  },
  secondary: {
    kMwMTN: "xv9yike",
    $$css: true
  },
  tertiary: {
    kMwMTN: "xv9yike",
    $$css: true
  },
  disabled: {
    kMwMTN: "xqa6c3m",
    $$css: true
  },
  accent: {
    kMwMTN: "xqwr325",
    $$css: true
  },
  success: {
    kMwMTN: "xtjic6",
    $$css: true
  },
  error: {
    kMwMTN: "xjt36v0",
    $$css: true
  },
  warning: {
    kMwMTN: "xs3pv69",
    $$css: true
  },
  inherit: {
    kMwMTN: "x1heor9g",
    $$css: true
  },
  blue: {
    kMwMTN: "x1fns2mt",
    $$css: true
  },
  red: {
    kMwMTN: "xeffzf7",
    $$css: true
  },
  green: {
    kMwMTN: "xmxeech",
    $$css: true
  },
  gray: {
    kMwMTN: "x1eyinzz",
    $$css: true
  },
  cyan: {
    kMwMTN: "x157w0xa",
    $$css: true
  },
  teal: {
    kMwMTN: "x1f3zxcb",
    $$css: true
  },
  yellow: {
    kMwMTN: "x1g6zdft",
    $$css: true
  },
  orange: {
    kMwMTN: "xxu74a4",
    $$css: true
  },
  pink: {
    kMwMTN: "x1kxxfg5",
    $$css: true
  },
  purple: {
    kMwMTN: "xzdw94u",
    $$css: true
  }
};

/**
 * Size styles for direct SVG icon components.
 * Uses width/height only — SVG components handle their own viewBox scaling.
 */
const sizeStyles = {
  xsm: {
    kzqmXN: "xsmyaan",
    kZKoxP: "x1kpxq89",
    $$css: true
  },
  sm: {
    kzqmXN: "x1kky2od",
    kZKoxP: "xlup9mm",
    $$css: true
  },
  md: {
    kzqmXN: "xw4jnvo",
    kZKoxP: "x1qx5ct2",
    $$css: true
  },
  lg: {
    kzqmXN: "xvy4d1p",
    kZKoxP: "xxk0z11",
    $$css: true
  }
};

/**
 * Size styles for string-based (registry) icons.
 * Includes fontSize so that 1em-based icons from the registry scale correctly.
 */
const spanSizeStyles = {
  xsm: {
    kzqmXN: "xsmyaan",
    kZKoxP: "x1kpxq89",
    kGuDYH: "xfifm61",
    $$css: true
  },
  sm: {
    kzqmXN: "x1kky2od",
    kZKoxP: "xlup9mm",
    kGuDYH: "x1j61zf2",
    $$css: true
  },
  md: {
    kzqmXN: "xw4jnvo",
    kZKoxP: "x1qx5ct2",
    kGuDYH: "xwsyq91",
    $$css: true
  },
  lg: {
    kzqmXN: "xvy4d1p",
    kZKoxP: "xxk0z11",
    kGuDYH: "x1pvqxga",
    $$css: true
  }
};

// =============================================================================
// Types
// =============================================================================

/**
 * Type for icon components that can be passed to Icon.
 * Use this type when accepting an icon prop in other components.
 */

/**
 * Props for Icon component.
 * Extends SVGProps to allow passing additional SVG attributes (used when icon is a component).
 */

// =============================================================================
// Component
// =============================================================================

/**
 * Renders an icon from the icon registry or a custom SVG component.
 *
 * @example
 * ```
 * <Icon icon="close" size="md" color="primary" />
 * ```
 */
export function Icon({
  icon,
  color = 'inherit',
  size = 'md',
  ref,
  ...props
}) {
  // String mode: resolve from icon registry, wrap in styled span
  if (typeof icon === 'string') {
    return /*#__PURE__*/_jsx(IconFromRegistry, {
      name: icon,
      color: color,
      size: size,
      spanProps: props
    });
  }

  // Component mode: render SVG component directly with ref forwarding
  const IconComponent = icon;
  return /*#__PURE__*/_jsx(IconComponent, {
    ref: ref,
    "aria-hidden": "true",
    ...mergeProps(themeProps('icon', {
      size,
      color
    }), stylex.props(styles.root, colorStyles[color], sizeStyles[size])),
    ...props
  });
}
Icon.displayName = 'Icon';

// =============================================================================
// Internal: Registry Icon Renderer
// =============================================================================

/**
 * Internal component that resolves a semantic icon name from the registry
 * and renders it in a styled span with proper sizing.
 *
 * Extracted as a separate component so getIcon is only called
 * when the icon prop is a string.
 */
function IconFromRegistry({
  name,
  color,
  size,
  spanProps
}) {
  const resolvedIcon = getIcon(name);
  if (resolvedIcon == null) {
    return null;
  }
  return /*#__PURE__*/_jsx("span", {
    // Decorative by default, but placed BEFORE the prop spread so consumers
    // can override it (e.g. `aria-hidden={false}` + `role="img"` +
    // `aria-label`) to expose a meaningful standalone icon. This matches
    // component-mode Icon, which already sets aria-hidden before its spread.
    "aria-hidden": "true",
    ...spanProps,
    ...mergeProps(themeProps('icon', {
      size,
      color
    }), stylex.props(styles.span, colorStyles[color], spanSizeStyles[size])),
    children: resolvedIcon
  });
}

/**
 * Renders an icon slot value. Handles semantic names, ReactNode values, and
 * component types:
 * - If the value is a semantic icon name string, wraps it in Icon.
 * - If the value is a component (function or forwardRef object), wraps it in Icon.
 * - Otherwise, renders the ReactNode directly.
 */
export function renderIconSlot(icon, props) {
  if (typeof icon === 'string') {
    return /*#__PURE__*/_jsx(Icon, {
      icon: icon,
      ...props
    });
  }
  if (typeof icon === 'function' || typeof icon === 'object' && icon !== null && 'render' in icon) {
    return /*#__PURE__*/_jsx(Icon, {
      icon: icon,
      ...props
    });
  }
  return icon;
}